/**
 * Object sprite resolution — shared by the Interpreter (initial show/present)
 * and the ObjectSpriteEngine (reactive re-skin). Single source of truth so the
 * two paths can never disagree on which variant/asset an object resolves to.
 */
import { evaluate } from "./ExprEval";
import { Ternary } from "./Ternary";
import type { Blackboard } from "./Blackboard";
import type { ObjectEntity } from "./Interpreter";

function isTrue(ast: import("./ExprEval").AstNode, bb: Blackboard): boolean {
  const r = evaluate(ast, bb);
  return (r instanceof Ternary ? r : Ternary.from(r)).isTrue;
}

/** First `sprite_rules` entry whose condition is T selects its variant, else "default". */
export function resolveObjectVariant(entity: ObjectEntity | undefined, bb: Blackboard): string {
  for (const rule of entity?.sprite_rules ?? []) {
    if (isTrue(rule.cond_ast, bb)) return rule.variant;
  }
  return "default";
}

/** Map a variant name to its sprite asset path (".png" appended if no extension). */
export function resolveObjectAsset(entity: ObjectEntity | undefined, variant: string): string {
  const sprites = entity?.sprites;
  let path = sprites?.[variant] ?? sprites?.["default"] ?? "";
  if (path && !path.includes(".")) path += ".png";
  return path;
}

/** Resolve the active sprite asset for an object given current Blackboard state. */
export function resolveObjectSprite(entity: ObjectEntity | undefined, bb: Blackboard): string {
  return resolveObjectAsset(entity, resolveObjectVariant(entity, bb));
}

/** True when the object's `hidden_when` condition is currently T. */
export function isObjectHidden(entity: ObjectEntity | undefined, bb: Blackboard): boolean {
  return entity?.hidden_when_ast ? isTrue(entity.hidden_when_ast, bb) : false;
}
